/**
 * Deterministic classification of the legacy generic equipment-catalog rows.
 *
 * The migration rule (see the equipment-library architecture doc):
 *   - physical OEM alternatives   → OEM library (canonical products/revisions);
 *   - civil, logistics, commissioning, connectivity services, and generic
 *     commercial assumptions       → planning_benchmarks;
 *   - unsupported placeholders     → historical-only, never an OEM product.
 *
 * This module keeps generic planning rows out of the OEM product library and
 * materializes the generation/storage planning archetypes as canonical
 * `EquipmentPlanningBenchmark` records (indicative CAPEX + lead time, clearly
 * labelled non-OEM). Infrastructure and service rows (grid, cooling, IT, civil,
 * logistics, commissioning, connectivity) are classified into the planning
 * manifest so they can never masquerade as OEM equipment.
 */
import type {
  EquipmentPlanningBenchmark,
  EquipmentSource,
  EquipmentTechnology,
  PlanningBenchmarkMetric,
} from '../../types/equipmentLibrary';
import { EQUIPMENT_LIBRARY_SCHEMA_VERSION } from '../../types/equipmentLibrary';
import {
  sealEquipmentPlanningBenchmark,
  sealEquipmentSource,
} from '../../lib/equipmentLibrary/digests';
import { LEAD_TIME_BASELINES } from '../../lib/supplyChainBaselines';
import { OEM_GENERATION_SOURCE_REGISTER } from '../../lib/generationImpact/generationOemSourceRegister';

const COMPILED_AT = OEM_GENERATION_SOURCE_REGISTER.compiledAt;
const CURATOR_ID = 'deep-atomic-equipment-curation';

export type LegacyRowDisposition =
  | 'oem-library'
  | 'planning-benchmark'
  | 'historical-only';

export type PlanningBenchmarkClass =
  | 'generation'
  | 'energy-storage'
  | 'grid-infrastructure'
  | 'cooling'
  | 'data-center-it'
  | 'connectivity'
  | 'civil'
  | 'logistics'
  | 'commissioning';

const CATEGORY_TO_CLASS: Readonly<Record<string, PlanningBenchmarkClass>> = {
  'power-generation': 'generation',
  'energy-storage': 'energy-storage',
  'power-conversion': 'grid-infrastructure',
  cooling: 'cooling',
  'data-center-it': 'data-center-it',
  connectivity: 'connectivity',
  'civil-construction': 'civil',
  logistics: 'logistics',
  commissioning: 'commissioning',
};

/**
 * Explicit generation/storage technology per baseline key. Only keys with an
 * unambiguous generation technology are materialized as canonical benchmarks;
 * everything else stays a manifest-only planning row (see below). Never guessed.
 */
const KEY_TO_TECHNOLOGY: Readonly<Record<string, EquipmentTechnology>> = {
  'gas-turbine-h-class': 'natural-gas-combined-cycle',
  'gas-turbine-f-class': 'natural-gas-combined-cycle',
  'gas-turbine-aeroderivative': 'natural-gas-simple-cycle',
  'hrsg-triple-pressure': 'natural-gas-combined-cycle',
  'steam-turbine-reheat': 'natural-gas-combined-cycle',
  'condenser-water-cooled': 'natural-gas-combined-cycle',
  'condenser-air-cooled': 'natural-gas-combined-cycle',
  'smr-module-nuscale-class': 'nuclear-pwr',
  'smr-module-x-energy-class': 'nuclear-htgr',
  'smr-module-bwxt-class': 'nuclear-microreactor',
  'smr-control-rod-drive-mechanism': 'nuclear-pwr',
  'smr-reactor-cooling-pump': 'nuclear-pwr',
  'smr-steam-generator': 'nuclear-pwr',
  'smr-containment-vessel': 'nuclear-pwr',
  'pv-module-bifacial': 'solar-pv',
  'pv-tracker-single-axis': 'solar-pv',
  'pv-string-inverter': 'solar-pv',
  'pv-central-inverter': 'solar-pv',
  'wind-turbine-onshore-class-iii': 'wind-onshore',
  'wind-turbine-onshore-class-ii': 'wind-onshore',
  'wind-turbine-offshore': 'wind-offshore',
  'bess-lfp-container-4hr': 'battery-storage',
  'bess-lfp-container-2hr': 'battery-storage',
  'bess-pcs-power-conversion': 'battery-storage',
  'bess-ems-controller': 'battery-storage',
  'flywheel-storage': 'flywheel-storage',
  'thermal-storage-molten-salt': 'thermal-storage',
};

export const LEGACY_PLANNING_SOURCE_ID = 'deep-atomic-supply-chain-planning-baseline';

export function legacyPlanningBaselineSource(): EquipmentSource {
  return sealEquipmentSource({
    schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
    id: LEGACY_PLANNING_SOURCE_ID,
    kind: 'planning-study',
    publisher: 'Deep Atomic',
    title: 'Supply-chain planning baseline (indicative CAPEX and lead time)',
    retrievedAt: COMPILED_AT,
    access: 'public',
    visibility: 'public',
    url: 'https://datacrate.io/methodology/supply-chain-planning-baseline',
    notes: 'Indicative, non-OEM planning assumptions. Not a manufacturer quote; class-level archetypes only.',
  });
}

/** Legacy `EquipmentCatalogEntry`-shaped row (only the fields we classify on). */
export interface LegacyCatalogRowLike {
  readonly id: string;
  readonly spec?: string;
  readonly source?: string;
  readonly assumed?: boolean;
  readonly generationScreeningId?: string;
  readonly oemGenerationProductId?: string;
}

export interface LegacyRowClassification {
  readonly id: string;
  readonly disposition: LegacyRowDisposition;
  readonly reason: string;
}

/**
 * Classify one derived catalog row. Anything not proven to be an OEM product or
 * an unsupported placeholder defaults to a planning benchmark — a generic row
 * can never fall through into the OEM product library.
 */
export function classifyLegacyCatalogRow(
  row: LegacyCatalogRowLike,
): LegacyRowClassification {
  if (
    row.source === 'oem-source-register'
    || row.oemGenerationProductId != null
    || row.id.startsWith('catalog-oem-generation-')
  ) {
    return {
      id: row.id,
      disposition: 'oem-library',
      reason: 'Physical OEM product identity normalized into the OEM library.',
    };
  }
  if (
    row.assumed === true
    || row.id.startsWith('catalog-generation-assumed-')
    || (row.spec ?? '').startsWith('ASSUMED')
  ) {
    return {
      id: row.id,
      disposition: 'historical-only',
      reason: 'Unsupported placeholder; retained as history and never an OEM product.',
    };
  }
  if (row.generationScreeningId != null || row.id.startsWith('catalog-generation-screening-')) {
    return {
      id: row.id,
      disposition: 'oem-library',
      reason: 'Generation & Impact v1 envelope preserved as a canonical generation-screening admission.',
    };
  }
  return {
    id: row.id,
    disposition: 'planning-benchmark',
    reason: 'Generic, class-level commercial assumption; not a specific OEM product.',
  };
}

export interface LegacyPlanningRow {
  readonly id: string;
  readonly baselineKey: string;
  readonly name: string;
  readonly planningClass: PlanningBenchmarkClass;
  readonly indicativeUnitCostUsd: number | null;
  readonly leadTimeMonths: { readonly low: number; readonly high: number };
  /** Present only when the row maps to a generation/storage technology. */
  readonly benchmarkId: string | null;
}

/** Full planning-row manifest for every generic archetype (all 9 classes). */
export function legacyPlanningRowManifest(): readonly LegacyPlanningRow[] {
  return LEAD_TIME_BASELINES.map(baseline => {
    const planningClass = CATEGORY_TO_CLASS[baseline.category] ?? 'grid-infrastructure';
    const technology = KEY_TO_TECHNOLOGY[baseline.key];
    return {
      id: `planning-${baseline.key}`,
      baselineKey: baseline.key,
      name: baseline.name,
      planningClass,
      indicativeUnitCostUsd: baseline.unitCostUsd ?? null,
      leadTimeMonths: { low: baseline.leadTimeMonthsLow, high: baseline.leadTimeMonthsHigh },
      benchmarkId: technology ? `planning-benchmark-${baseline.key}` : null,
    };
  });
}

function planningMetrics(
  baseline: (typeof LEAD_TIME_BASELINES)[number],
): PlanningBenchmarkMetric[] {
  const metrics: PlanningBenchmarkMetric[] = [];
  if (baseline.unitCostUsd != null) {
    metrics.push({
      id: `${baseline.key}:indicative-capex`,
      metric: 'custom:indicative-unit-capex',
      value: { kind: 'scalar', value: baseline.unitCostUsd },
      unit: 'USD',
      basis: {
        qualifier: 'typical',
        configuration: 'class-level archetype',
        notes: 'Indicative order-of-magnitude planning cost; not an OEM quote.',
      },
      sourceIds: [LEGACY_PLANNING_SOURCE_ID],
      confidence: 'low',
    });
  }
  metrics.push({
    id: `${baseline.key}:lead-time`,
    metric: 'custom:lead-time-months',
    value: { kind: 'range', low: baseline.leadTimeMonthsLow, high: baseline.leadTimeMonthsHigh },
    unit: 'month',
    basis: { qualifier: 'range', configuration: 'class-level archetype' },
    sourceIds: [LEGACY_PLANNING_SOURCE_ID],
    confidence: 'low',
  });
  return metrics;
}

/**
 * Canonical planning benchmarks for the generation/storage archetypes. Rows
 * without a generation technology stay manifest-only (see the manifest), so no
 * civil/logistics/service row is forced into a generation-technology record.
 */
export function buildLegacyPlanningBenchmarks(): EquipmentPlanningBenchmark[] {
  return LEAD_TIME_BASELINES.flatMap(baseline => {
    const technology = KEY_TO_TECHNOLOGY[baseline.key];
    if (!technology) return [];
    return [sealEquipmentPlanningBenchmark({
      schemaVersion: EQUIPMENT_LIBRARY_SCHEMA_VERSION,
      id: `planning-benchmark-${baseline.key}`,
      name: baseline.name,
      technology,
      geography: 'global',
      scenario: 'indicative-planning-2026q3',
      sourceIds: [LEGACY_PLANNING_SOURCE_ID],
      metrics: planningMetrics(baseline),
      compiledAt: COMPILED_AT,
      reviewedAt: COMPILED_AT,
      reviewedBy: CURATOR_ID,
    })];
  });
}
